import React, { createContext, useCallback, useEffect, useState } from 'react';
import cacheService from '../../../services/cacheService';
import eventBus from '../../../services/globalEventBus';
import userService from '../../../services/userService';
import { logger } from '../../../utils/logger';
import { useUser, type User } from '../../authentication';
import { getOrg, GetOrgResponse } from '../api/getOrg';
import { Org } from '../models/Org';
import { transformOrg } from '../utils/transformOrg';

export type OrgLoadError = 'no_user' | 'no_org' | 'org_load_fail';

export type OrgContextValue = {
  org?: Org;
  isLoading: boolean;
  error?: OrgLoadError;
  updateOrg: (updater: (prev: Org) => Org) => void;
};

const defaultContextValue: OrgContextValue = {
  org: undefined,
  isLoading: false,
  error: undefined,
  updateOrg: () => undefined,
};

export const OrgContext = createContext<OrgContextValue>(defaultContextValue);
OrgContext.displayName = 'OrgContext';

type OrgProviderProps = {
  children: React.ReactNode;
};

type SessionOrgUser = Pick<User, 'userId' | 'lastSelectedAccount' | 'accountIds'>;

type LoadedOrgState = {
  org?: Org;
  isLoading: boolean;
  error?: OrgLoadError;
};

function toOrgLoadError(reason: unknown): OrgLoadError {
  if (reason instanceof Error && reason.message === 'No account ID') {
    return 'no_org';
  }
  return 'org_load_fail';
}

export const OrgProvider = ({ children }: OrgProviderProps) => {
  const { user, refreshUser } = useUser();
  const [orgState, setOrgState] = useState<LoadedOrgState>({
    isLoading: true,
  });

  const updateOrg = (updater: (prev: Org) => Org) => {
    setOrgState(prev => (prev.org ? { ...prev, org: updater(prev.org) } : prev));
  };

  const switchOrg = useCallback((currentUser: SessionOrgUser): Promise<void> => {
    const firstAccountId = currentUser.accountIds?.find(account => account.id)?.id;
    if (!firstAccountId) {
      return Promise.reject(new Error('No account ID'));
    }
    // Fires useEffect event 'AccountSelected' to load the org state.
    return userService.switchOrg(currentUser.userId, firstAccountId);
  }, []);

  const loadOrg = useCallback(
    (currentUser: SessionOrgUser): Promise<Org | null> => {
      const selectedAccount = currentUser.lastSelectedAccount;
      const hasAccountIds = !!currentUser.accountIds?.length;

      if (!selectedAccount && !hasAccountIds) {
        return Promise.reject(new Error('No account ID'));
      }

      // If no selected account, switch org and hand off to AccountSelected → refreshUser.
      if (!selectedAccount) {
        return switchOrg(currentUser)
          .then(() => null)
          .catch(reason => {
            logger.error('Failed to switch org', reason);
            return Promise.reject(reason);
          });
      }

      return getOrg(selectedAccount)
        .then((response: GetOrgResponse) => {
          const transformedOrg = transformOrg(response);
          if (!transformedOrg) {
            return Promise.reject(new Error('No org'));
          }

          return transformedOrg;
        })
        .catch(reason => {
          logger.error('Failed to load org state', reason);
          return Promise.reject(new Error('Failed to load org state'));
        });
    },
    [switchOrg]
  );

  const handleAccountOrInviteChange = useCallback(() => {
    cacheService.removeAll();
    setOrgState(prev => ({ ...prev, isLoading: true, error: undefined }));
    refreshUser().catch(reason => {
      logger.error('Failed to refresh user', reason);
      setOrgState(prev => ({ ...prev, isLoading: false, error: 'org_load_fail' }));
    });
  }, [refreshUser]);

  useEffect(() => {
    if (!user) {
      setOrgState({ isLoading: false, error: 'no_user' });
      return;
    }

    let cancelled = false;
    setOrgState(prev => ({
      ...prev,
      isLoading: true,
      error: undefined,
    }));

    loadOrg(user)
      .then(org => {
        if (cancelled || !org) {
          // null = switchOrg handoff; AccountSelected → refreshUser reloads org.
          return;
        }
        setOrgState({ org, isLoading: false, error: undefined });
      })
      .catch(reason => {
        if (!cancelled) {
          setOrgState(prev => ({ ...prev, org: undefined, isLoading: false, error: toOrgLoadError(reason) }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, loadOrg]);

  useEffect(() => {
    // When the account is selected or an invite is accepted, reload the org state.
    const unsubscribers = [
      eventBus.subscribe('AccountSelected', handleAccountOrInviteChange),
      eventBus.subscribe('InviteAccepted', handleAccountOrInviteChange),
    ];

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [handleAccountOrInviteChange]);

  return (
    <OrgContext.Provider
      value={{
        org: orgState.org,
        isLoading: orgState.isLoading,
        error: orgState.error,
        updateOrg,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};
